import { giftBoxPage } from "@/mocks/giftBox";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "冷凍宅配與禮盒｜來加綉綉紅豆餅",
  description: "來加綉綉紅豆餅提供冷凍宅配、節慶禮盒、企業下午茶與團購訂購服務，讓顧客在家中、辦公室與聚會場合也能享受手作紅豆餅的香氣。",
  keywords: "紅豆餅禮盒,冷凍紅豆餅,紅豆餅宅配,紅豆餅團購,企業下午茶,節慶禮盒,紅豆餅配送",
  ogType: "product",
};

const planImages = [
  "https://readdy.ai/api/search-image?query=red%20bean%20cake%20gift%20box%20packaging%20assorted%20taiyaki%20flavors%20red%20bean%20custard%20taro%20beautiful%20gift%20box%20with%20ribbon%20warm%20golden%20lighting%20Japanese%20Taiwanese%20dessert%20gift%20elegant%20food%20photography%20cozy%20bakery%20presentation&width=600&height=400&seq=gb-plan-1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=classic%20red%20bean%20taiyaki%20box%20packaging%20traditional%20style%20golden%20brown%20Japanese%20pastry%20gift%20box%20warm%20amber%20lighting%20premium%20food%20gift%20box%20artisan%20handmade%20dessert%20elegant&width=600&height=400&seq=gb-plan-2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=corporate%20event%20buffet%20dessert%20table%20taiyaki%20red%20bean%20cakes%20on%20elegant%20serving%20platter%20office%20party%20catering%20warm%20lighting%20group%20sharing%20food%20Japanese%20street%20snack&width=600&height=400&seq=gb-plan-3&orientation=landscape",
];

const situationIcons = [
  { icon: "ri-home-heart-line", label: "家庭點心" },
  { icon: "ri-building-2-line", label: "公司下午茶" },
  { icon: "ri-gift-2-line", label: "節慶送禮" },
  { icon: "ri-store-2-line", label: "市集與活動訂購" },
];

export default function GiftBoxPage() {
  const { hero, situations, plans, reheat, orderFlow } = giftBoxPage;

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=red%20bean%20cake%20gift%20box%20frozen%20delivery%20packaging%20with%20beautiful%20wrapped%20boxes%20cozy%20home%20kitchen%20atmosphere%20warm%20golden%20lighting%20Japanese%20taiyaki%20dessert%20gift%20set%20elegant%20food%20photography%20artisan%20sweet%20gift&width=1600&height=900&seq=gb-hero-1&orientation=landscape"
            alt="冷凍宅配紅豆餅禮盒包裝展示"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 max-w-3xl mx-auto text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-2xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="https://lin.ee/xiNNjjf"
              rel="nofollow"
              target="_blank"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-redbean-gold text-redbean-coffee font-semibold rounded-full hover:bg-redbean-cream transition-colors whitespace-nowrap text-[1.0625rem] cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-truck-line" />
              </span>
              {hero.ctaPrimary}
            </a>
            <a
              href="https://lin.ee/xiNNjjf"
              rel="nofollow"
              target="_blank"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-redbean-cream text-redbean-cream font-semibold rounded-full hover:bg-redbean-cream hover:text-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem] cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-gift-2-line" />
              </span>
              {hero.ctaSecondary}
            </a>
          </div>
        </div>
      </section>

      {/* Situations */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {situations.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {situations.items.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <i className={`${situationIcons[idx].icon} text-2xl`} />
                    </span>
                  </div>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {plans.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {plans.plans.map((plan, idx) => (
              <ScrollReveal key={idx} delay={idx * 120}>
                <div className="bg-white rounded-card overflow-hidden shadow-soft flex flex-col">
                  <div className="w-full" style={{ height: "200px" }}>
                    <img
                      src={planImages[idx]}
                      alt={plan.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <h3
                      className="font-heading font-bold text-redbean-coffee mb-2"
                      style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                    >
                      {plan.name}
                    </h3>
                    <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed mb-3">
                      {plan.desc}
                    </p>
                    <p className="text-redbean-caramel text-sm mb-5">
                      <span className="font-semibold">適合：</span>
                      {plan.audience}
                    </p>
                    <div className="mt-auto">
                      <a
                        href="https://lin.ee/xiNNjjf"
                        rel="nofollow"
                        target="_blank"
                        className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 bg-redbean-primary text-redbean-cream font-semibold rounded-full hover:bg-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem] cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-chat-1-line" />
                        </span>
                        {plan.cta}
                      </a>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={200}>
            <p className="text-center text-redbean-coffee/40 text-sm mt-8">
              {plans.disclaimer}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Reheat Steps */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {reheat.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {reheat.steps.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft relative">
                  <span className="inline-block px-3 py-1 bg-redbean-gold/20 text-redbean-caramel text-sm font-bold rounded-full mb-4">
                    {item.step}
                  </span>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={200}>
            <p className="text-center text-redbean-coffee/40 text-sm mt-8">
              {reheat.note}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Order Flow */}
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-14"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {orderFlow.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
            {orderFlow.steps.map((step, idx) => (
              <ScrollReveal key={idx} delay={idx * 80}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-redbean-primary text-redbean-cream font-bold text-lg">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-redbean-coffee text-[1.0625rem] font-medium leading-relaxed">
                      {step}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <GlobalBottomCta />
    </main>
  );
}